import {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import Cookies from "js-cookie";
import { message } from "antd";
import rolesPermissions from "../data/roles&Permissions";
import { useQueryClient } from "@tanstack/react-query";
import SESSION_CONFIG from "../config/sessionConfig";
import { jwtDecode } from "jwt-decode";

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState({});
  const [loading, setLoading] = useState(true);
  const [sessionWarningShown, setSessionWarningShown] = useState(false);
  const queryClient = useQueryClient();
  
  const sessionCheckIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  /**
   * Validate JWT token expiration
   */
  const isTokenExpired = useCallback((token) => {
    if (!token) return true;
    
    try {
      const decoded = jwtDecode(token);
      const now = Date.now() / 1000; // JWT exp is in seconds
      
      // Check if token has exp claim and if it's expired
      if (decoded.exp && decoded.exp < now) {
        console.log("JWT token is expired");
        return true;
      }
      
      return false;
    } catch (error) {
      console.error("Error decoding token:", error);
      return true; // Treat invalid tokens as expired
    }
  }, []);

  /**
   * Clean up all authentication data - IMPROVED
   */
  const clearAuthData = useCallback(() => {
    // Remove cookie explicitly
    Cookies.remove("_smartweld_Cookie", { path: "/" });
    
    // Also try removing from all possible paths (belt and suspenders approach)
    Cookies.remove("_smartweld_Cookie");
    
    // Clear session storage
    sessionStorage.removeItem("_smartWeldUser");
    sessionStorage.removeItem("sessionExpire");
    
    // Clear React Query cache
    queryClient.clear();
    
    // Clear auth state
    setAuth({});
    setSessionWarningShown(false);
    
    // Verify cookie is actually removed
    const stillExists = Cookies.get("_smartweld_Cookie");
    if (stillExists) {
      console.error("⚠️ WARNING: Cookie was not removed successfully!");
    } else {
      console.log("✅ Auth data and token cleared successfully");
    }
  }, [queryClient]);

  /**
   * Handle user logout - IMPROVED
   */
  const logout = useCallback(() => {
    console.log("User logging out");
    
    // Clear all auth data first
    clearAuthData();
    
    // Show message
    message.info("You have been logged out");
    
    // Small delay to ensure cleanup completes
    setTimeout(() => {
      window.location.href = "/login";
    }, 100);
  }, [clearAuthData]);

  /**
   * Check if session is expired - IMPROVED
   */
  const isSessionExpired = useCallback(() => {
    const expireTime = sessionStorage.getItem("sessionExpire");
    const token = Cookies.get("_smartweld_Cookie");
    
    // No expiration time = expired
    if (!expireTime) {
      console.log("No expiration time found");
      return true;
    }
    
    // No token = expired
    if (!token) {
      console.log("No token found in cookies");
      return true;
    }
    
    // Check if JWT token itself is expired
    if (isTokenExpired(token)) {
      console.log("JWT token has expired");
      return true;
    }
    
    // Parse session expiry timestamp
    const expirationTimestamp = Number(expireTime);
    
    if (isNaN(expirationTimestamp) || expirationTimestamp <= 0) {
      console.error("Invalid expiration timestamp:", expireTime);
      return true;
    }
    
    // Check session timestamp
    const now = Date.now();
    const isExpired = now >= expirationTimestamp;
    
    if (isExpired) {
      console.log("Session expired:", {
        now: new Date(now).toLocaleString(),
        expiry: new Date(expirationTimestamp).toLocaleString()
      });
    }
    
    return isExpired;
  }, [isTokenExpired]);

  /**
   * Get remaining session time in milliseconds
   */
  const getSessionTimeRemaining = useCallback(() => {
    const expireTime = sessionStorage.getItem("sessionExpire");
    if (!expireTime) return 0;

    const expirationTimestamp = Number(expireTime);
    if (isNaN(expirationTimestamp)) return 0;

    const now = Date.now();
    return Math.max(0, expirationTimestamp - now);
  }, []);

  /**
   * Extend session on user activity - IMPROVED
   */
  const extendSession = useCallback(() => {
    if (!SESSION_CONFIG.EXTEND_ON_ACTIVITY) return;
    if (!auth.accessToken) return;

    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;

    // Only extend if enough time has passed
    if (timeSinceLastActivity < SESSION_CONFIG.MIN_EXTEND_INTERVAL) return;

    // Check if current session is still valid
    if (isSessionExpired()) {
      console.log("Cannot extend expired session");
      logout(); // Explicitly logout if trying to extend expired session
      return;
    }

    const newExpirationTime = now + SESSION_CONFIG.ACTIVITY_EXTEND_DURATION;
    
    // Update cookie and session storage
    const token = Cookies.get("_smartweld_Cookie");
    if (token) {
      const expirationDate = new Date(newExpirationTime);
      
      // Set cookie with explicit path
      Cookies.set("_smartweld_Cookie", token, { 
        expires: expirationDate,
        path: "/"
      });
      
      sessionStorage.setItem("sessionExpire", newExpirationTime.toString());
      
      lastActivityRef.current = now;
      setSessionWarningShown(false);
      
      console.log("✅ Session extended until:", expirationDate.toLocaleString());
    } else {
      console.error("⚠️ Token not found when trying to extend session");
      logout();
    }
  }, [auth.accessToken, isSessionExpired, logout]);

  /**
   * Monitor session expiration - IMPROVED
   */
  const checkSession = useCallback(() => {
    // Don't run session checks on public pages
    const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password'];
    const currentPath = window.location.pathname.toLowerCase();
    const isPublicPage = publicPaths.some(path => currentPath.includes(path));
    
    if (isPublicPage) {
      return; // Skip session checks on public pages
    }

    if (!auth.accessToken) return;

    // Verify token still exists
    const token = Cookies.get("_smartweld_Cookie");
    if (!token) {
      console.error("⚠️ Token missing from cookies during session check");
      clearAuthData();
      message.error("Session token was removed. Please log in again.", 5);
      setTimeout(() => {
        window.location.href = "/login";
      }, 100);
      return;
    }

    // Check if session expired
    if (isSessionExpired()) {
      console.log("Session expired, logging out");
      clearAuthData();
      message.error("Your session has expired. Please log in again.", 5);
      
      setTimeout(() => {
        window.location.href = "/login";
      }, 100);
      return;
    }

    // Show warning if session is about to expire
    const timeRemaining = getSessionTimeRemaining();
    if (
      timeRemaining > 0 && 
      timeRemaining <= SESSION_CONFIG.WARNING_TIME && 
      !sessionWarningShown
    ) {
      const minutesRemaining = Math.ceil(timeRemaining / 60000);
      message.warning(
        `Your session will expire in ${minutesRemaining} minute(s). Please save your work.`,
        10
      );
      setSessionWarningShown(true);
    }
  }, [
    auth.accessToken, 
    isSessionExpired, 
    getSessionTimeRemaining, 
    clearAuthData, 
    sessionWarningShown
  ]);

  /**
   * Initialize user session on component mount - IMPROVED
   */
  useEffect(() => {
    const checkExistingAuth = async () => {
      try {
        // Don't run auth checks on login/public pages
        const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password'];
        const currentPath = window.location.pathname.toLowerCase();
        const isPublicPage = publicPaths.some(path => currentPath.includes(path));
        
        if (isPublicPage) {
          console.log("On public page, skipping auth check");
          setLoading(false);
          return;
        }

        const token = Cookies.get("_smartweld_Cookie");
        const userData = sessionStorage.getItem("_smartWeldUser");
        const expireTime = sessionStorage.getItem("sessionExpire");

        console.log("Checking existing auth:", { 
          hasToken: !!token, 
          hasUserData: !!userData, 
          hasExpireTime: !!expireTime 
        });

        if (token && userData && expireTime) {
          // Validate JWT token first
          if (isTokenExpired(token)) {
            console.log("Stored token is expired, clearing auth");
            clearAuthData();
            setLoading(false);
            return;
          }

          // Validate expiration time
          const expirationTimestamp = Number(expireTime);
          
          if (isNaN(expirationTimestamp) || expirationTimestamp <= 0) {
            console.error("Invalid session data, clearing auth");
            clearAuthData();
            setLoading(false);
            return;
          }

          // Check if session timestamp is expired
          const now = Date.now();
          if (now >= expirationTimestamp) {
            console.log("Session timestamp expired on mount, clearing auth");
            clearAuthData();
            setLoading(false);
            return;
          }

          try {
            const cookieData = JSON.parse(userData);
            const roleName = cookieData.roleName?.toUpperCase() || "USER";

            setAuth({
              accessToken: token,
              ...cookieData,
              role: roleName,
              permissions: rolesPermissions[roleName] || {
                titles: [],
                links: [],
              },
            });

            console.log("✅ Session restored successfully");
            const expiryDate = new Date(expirationTimestamp);
            console.log("Session expires at:", expiryDate.toLocaleString());
          } catch (error) {
            console.error("Error parsing stored user data:", error);
            clearAuthData();
          }
        } else {
          console.log("No existing session found");
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        clearAuthData();
      } finally {
        setLoading(false);
      }
    };

    checkExistingAuth();

    // Listen for auth errors from axios interceptor
    const handleAuthError = (event) => {
      console.log("Auth error event received:", event.detail);
      clearAuthData();
      message.error(event.detail?.message || "Authentication error");
    };

    window.addEventListener("authError", handleAuthError);

    return () => {
      window.removeEventListener("authError", handleAuthError);
    };
  }, [clearAuthData, isTokenExpired]);

  /**
   * Set up session monitoring and activity tracking
   */
  useEffect(() => {
    if (!auth.accessToken) {
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
        sessionCheckIntervalRef.current = null;
      }
      return;
    }

    console.log("Starting session monitoring...");
    sessionCheckIntervalRef.current = setInterval(
      checkSession, 
      SESSION_CONFIG.CHECK_INTERVAL
    );

    // Track user activity for sliding session
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    const handleActivity = () => {
      extendSession();
    };

    if (SESSION_CONFIG.EXTEND_ON_ACTIVITY) {
      console.log("Enabling sliding session on user activity");
      activityEvents.forEach(event => {
        window.addEventListener(event, handleActivity, { passive: true });
      });
    }

    return () => {
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
        console.log("Session monitoring stopped");
      }
      if (SESSION_CONFIG.EXTEND_ON_ACTIVITY) {
        activityEvents.forEach(event => {
          window.removeEventListener(event, handleActivity);
        });
      }
    };
  }, [auth.accessToken, checkSession, extendSession]);

  /**
   * Handle user login - IMPROVED
   */
  const userLogin = useCallback((accessToken, cookieData, expirationTimestamp) => {
    try {
      if (!accessToken || !cookieData || !expirationTimestamp) {
        console.error("Invalid login data provided");
        return false;
      }

      // Validate JWT token
      if (isTokenExpired(accessToken)) {
        console.error("Cannot login with expired token");
        return false;
      }

      const expireTime = Number(expirationTimestamp);
      if (isNaN(expireTime) || expireTime <= Date.now()) {
        console.error("Invalid expiration timestamp:", expirationTimestamp);
        return false;
      }

      const expirationDate = new Date(expireTime);
      
      // Store with explicit path
      Cookies.set("_smartweld_Cookie", accessToken, {
        expires: expirationDate,
        path: "/"
      });

      sessionStorage.setItem("_smartWeldUser", JSON.stringify(cookieData));
      sessionStorage.setItem("sessionExpire", expireTime.toString());

      const roleName = cookieData.roleName?.toUpperCase() || "USER";

      setAuth({
        accessToken,
        ...cookieData,
        role: roleName,
        permissions: rolesPermissions[roleName] || { titles: [], links: [] },
      });

      lastActivityRef.current = Date.now();
      setSessionWarningShown(false);

      console.log("✅ Login successful, session expires at:", expirationDate.toLocaleString());
      return true;
    } catch (error) {
      console.error("Login failed:", error);
      return false;
    }
  }, [isTokenExpired]);

  /**
   * Check if user has permission for a route
   */
  const hasPermission = useCallback(
    (requiredPath) => {
      if (!auth.permissions) return false;
      return auth.permissions.links.includes(requiredPath);
    },
    [auth.permissions]
  );

  /**
   * Check if user is authenticated - IMPROVED
   */
  const isAuthenticated = useCallback(() => {
    const token = Cookies.get("_smartweld_Cookie");
    return !!auth?.accessToken && !!token && !isSessionExpired();
  }, [auth?.accessToken, isSessionExpired]);

  const value = {
    auth,
    setAuth,
    loading,
    userLogin,
    logout,
    hasPermission,
    isAuthenticated,
    getSessionTimeRemaining,
    extendSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;